import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const AuditLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;
  
  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAdminEmail, setFilterAdminEmail] = useState('');
  
  const backendUrl = API_BASE;

  useEffect(() => {
    fetchAuditLogs();
  }, [currentPage, filterAction, filterEntity, filterAdminEmail]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Build query params
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString()
      });
      
      if (filterAction) params.append('action', filterAction);
      if (filterEntity) params.append('entity', filterEntity);
      if (filterAdminEmail) params.append('admin_email', filterAdminEmail);

      const response = await fetch(`${backendUrl}/api/admin/audit-logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (response.status === 403) {
        setError('Access denied. Only admins can view audit logs.');
        setLoading(false);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch audit logs');

      const data = await response.json();
      setLogs(data.logs || []);
      setTotalPages(data.pagination?.total_pages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilterAction('');
    setFilterEntity('');
    setFilterAdminEmail('');
    setCurrentPage(1);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatJSON = (obj) => {
    if (!obj || Object.keys(obj).length === 0) return null;
    return JSON.stringify(obj, null, 2);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading audit logs...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error && error === 'Access denied. Only admins can view audit logs.') {
    return (
      <AdminLayout>
        <div className="bg-white border-2 border-red-300 rounded-xl p-6">
          <div className="flex items-center gap-3 text-red-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h2 className="font-bold text-lg">Access Denied</h2>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Get unique values for filter dropdowns
  const uniqueActions = [...new Set(logs.map(log => log.action))].sort();
  const uniqueEntities = [...new Set(logs.map(log => log.entity))].sort();

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">Audit Logs</h1>
            <p className="text-[#6B7280]">View admin actions and changes</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Action</label>
              <select
                value={filterAction}
                onChange={(e) => {
                  setFilterAction(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="">All Actions</option>
                {uniqueActions.map(action => (
                  <option key={action} value={action}>{formatAction(action)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Entity</label>
              <select
                value={filterEntity}
                onChange={(e) => {
                  setFilterEntity(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="">All Entities</option>
                {uniqueEntities.map(entity => (
                  <option key={entity} value={entity}>{entity.charAt(0).toUpperCase() + entity.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Admin Email</label>
              <input
                type="text"
                value={filterAdminEmail}
                onChange={(e) => {
                  setFilterAdminEmail(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Filter by email..."
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleResetFilters}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] hover:border-[#1E40AF] text-[#6B7280] hover:text-[#1E40AF] font-semibold rounded-lg transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl overflow-hidden">
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-[#6B7280]">No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Date / Time</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Admin Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Action</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Entity</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Entity ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">IP Address</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => {
                    const rowKey = `${log.entity_id}-${log.created_at}-${index}`;
                    const isExpanded = expandedRow === rowKey;
                    const hasBefore = log.before && Object.keys(log.before).length > 0;
                    const hasAfter = log.after && Object.keys(log.after).length > 0;
                    
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
                          onClick={() => setExpandedRow(isExpanded ? null : rowKey)}
                        >
                          <td className="py-3 px-4 text-sm text-[#6B7280]">{formatDateTime(log.created_at)}</td>
                          <td className="py-3 px-4 text-sm text-[#111827] font-medium">{log.admin_email || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                              {formatAction(log.action)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-[#6B7280]">{log.entity ? log.entity.charAt(0).toUpperCase() + log.entity.slice(1) : 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-[#6B7280] font-mono text-xs">{log.entity_id || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-[#6B7280] font-mono text-xs">{log.ip_address || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm">
                            {(hasBefore || hasAfter) && (
                              <button className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold flex items-center gap-1">
                                {isExpanded ? 'Hide' : 'Show'} Details
                                <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (hasBefore || hasAfter) && (
                          <tr>
                            <td colSpan="7" className="p-4 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {hasBefore && (
                                  <div>
                                    <h4 className="font-semibold text-sm text-[#111827] mb-2">Before</h4>
                                    <pre className="bg-white border border-[#E5E7EB] rounded p-3 text-xs overflow-auto max-h-96">
                                      {formatJSON(log.before)}
                                    </pre>
                                  </div>
                                )}
                                {hasAfter && (
                                  <div>
                                    <h4 className="font-semibold text-sm text-[#111827] mb-2">After</h4>
                                    <pre className="bg-white border border-[#E5E7EB] rounded p-3 text-xs overflow-auto max-h-96">
                                      {formatJSON(log.after)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                              {log.user_agent && (
                                <div className="mt-2 text-xs text-[#6B7280]">
                                  <strong>User Agent:</strong> {log.user_agent}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between bg-white border-2 border-[#E5E7EB] rounded-xl p-4">
            <div className="text-sm text-[#6B7280]">
              Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of {total} logs
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border-2 border-[#E5E7EB] rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#1E40AF] hover:text-[#1E40AF] transition-colors"
              >
                Previous
              </button>
              <div className="px-4 py-2 border-2 border-[#1E40AF] rounded-lg font-semibold text-sm text-[#1E40AF]">
                Page {currentPage} of {totalPages}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border-2 border-[#E5E7EB] rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#1E40AF] hover:text-[#1E40AF] transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AuditLogs;
